import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileCheck2, Gavel, Loader2, RefreshCw, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money } from "@/lib/format";

const BAND_STYLE = {
  low: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  medium: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  high: "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  critical: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const OUTCOME_STYLE = {
  clear: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  review: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  block: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const ScoreBar = ({ score, band }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
      <div className={`h-full rounded-full transition-all duration-500 ${
        band === "critical" ? "bg-red-500" : band === "high" ? "bg-orange-500"
          : band === "medium" ? "bg-amber-500" : "bg-emerald-500"}`}
           style={{ width: `${Math.min(100, score)}%` }} />
    </div>
    <span className="font-mono text-[11px] text-foreground">{score}</span>
  </div>
);

export default function RiskAml() {
  const { can, user: me } = useAuth();
  const [data, setData] = useState(null);
  const detail = useRowDetail();
  const [watchlist, setWatchlist] = useState(null);
  const [busy, setBusy] = useState("");
  const [screen, setScreen] = useState({ name: "", country: "IN", entity_type: "organisation" });
  const [result, setResult] = useState(null);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState({ note: "", evidence_ref: "" });
  const [decision, setDecision] = useState({ outcome: "accept", rationale: "" });

  const load = useCallback(async () => {
    const [reg, wl] = await Promise.all([api.get("/risk/register"), api.get("/risk/watchlist")]);
    setData(reg.data);
    setWatchlist(wl.data);
  }, []);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  const runScreen = async () => {
    if (!screen.name.trim()) return;
    setBusy("screen");
    try {
      const res = await api.post("/risk/screen", screen);
      setResult(res.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const assessAll = async () => {
    setBusy("assess");
    try {
      const res = await api.post("/risk/assess-all");
      toast.success(`${res.data.assessed} donors re-scored`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const reassess = async (id) => {
    setBusy(id);
    try {
      const res = await api.post(`/risk/assess/${id}`);
      toast.success(`${res.data.subject_name} is ${res.data.band} risk (${res.data.score})`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const addNote = async () => {
    setBusy("note");
    try {
      await api.post(`/risk/edd/${active.case_ref}/note`, note);
      toast.success("Due-diligence note recorded");
      setNote({ note: "", evidence_ref: "" });
      const res = await api.get("/risk/register");
      setData(res.data);
      setActive(res.data.cases.find((c) => c.case_ref === active.case_ref));
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const decide = async () => {
    setBusy("decide");
    try {
      await api.post(`/risk/edd/${active.case_ref}/decision`, decision);
      toast.success(`Case ${decision.outcome === "escalate" ? "escalated to the board" : decision.outcome + "ed"}`);
      setActive(null);
      setDecision({ outcome: "accept", rationale: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const openCases = useMemo(
    () => (data?.cases || []).filter((c) => ["open", "escalated"].includes(c.status)), [data]);

  if (!data) return <Loading label="Loading the risk register" />;
  const c = data.counters;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-021 · Risk & Anti-Money-Laundering"
        title="Screen the name, score the risk, keep the file"
        description="Every donor is scored against jurisdiction, screening outcome, cash intensity, cross-border source, gift size and KYC completeness. A confirmed sanctions match is always critical and opens an enhanced due-diligence case with four-eyes closure."
        actions={can("risk", "write") && (
          <Button onClick={assessAll} disabled={busy === "assess"} data-testid="assess-all-button"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "assess" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-1.5 h-4 w-4" />} Re-score portfolio
          </Button>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-assessed" label="Donors scored" value={c.assessed} accent="sky" icon={ShieldCheck} />
        <Kpi testId="kpi-critical" label="Critical" value={c.critical} accent="rose" icon={ShieldAlert} />
        <Kpi testId="kpi-high" label="High" value={c.high} accent="amber" />
        <Kpi testId="kpi-open-cases" label="Open EDD cases" value={c.open_cases} accent="violet" icon={FileCheck2} />
        <Kpi testId="kpi-overdue" label="Past SLA" value={c.overdue_cases} accent="rose" icon={AlertTriangle} />
        <Kpi testId="kpi-watchlist" label="Watchlist names" value={c.watchlist_entries} accent="emerald" />
      </div>

      <Tabs defaultValue="register">
        <TabsList>
          <TabsTrigger value="register" data-testid="tab-risk-register">Risk register</TabsTrigger>
          <TabsTrigger value="screening" data-testid="tab-screening">Name screening</TabsTrigger>
          <TabsTrigger value="cases" data-testid="tab-edd-cases">EDD cases ({openCases.length})</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-watchlist">Watchlists</TabsTrigger>
        </TabsList>

        <TabsContent value="register" className="mt-4">
          <Panel testId="panel-risk-register" kicker="FR-RISK-001" title="Donor risk register">
            <Table testId="risk-register-table"
                   head={<><Th>Subject</Th><Th>Country</Th><Th>Score</Th><Th>Band</Th><Th>Screening</Th>
                     <Th>Lifetime value</Th><Th>Top factors</Th><Th>Assessed</Th><Th /></>}>
              {data.assessments.length === 0 && <Empty colSpan={9} label="Nothing scored yet — run a portfolio re-score" />}
              {data.assessments.map((a) => (
                <tr key={refOf(a)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(a, refOf(a)))} data-testid={`risk-row-${a.subject_id}`}>
                  <Td><div className="font-medium text-foreground">{a.subject_name}</div>
                    <div className="text-[11px] text-muted-foreground">{a.gift_count} gifts</div></Td>
                  <Td className="font-mono text-[11px]">{a.country}</Td>
                  <Td><ScoreBar score={a.score} band={a.band} /></Td>
                  <Td><Chip testId={`risk-band-${a.subject_id}`} className={BAND_STYLE[a.band]}>{a.band}</Chip></Td>
                  <Td><Chip className={OUTCOME_STYLE[a.screening_outcome]}>{a.screening_outcome}</Chip></Td>
                  <Td className="font-mono text-[11px]">{money(a.lifetime_value, "INR", true)}</Td>
                  <Td className="max-w-[260px]">
                    <div className="flex flex-wrap gap-1">
                      {(a.factors || []).filter((f) => f.points > 0).slice(0, 3).map((f) => (
                        <span key={f.factor} title={f.detail}
                              className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {f.factor} +{f.points}
                        </span>
                      ))}
                    </div>
                  </Td>
                  <Td className="font-mono text-[10.5px] text-muted-foreground">{dateTime(a.assessed_at)}</Td>
                  <Td>
                    {can("risk", "write") && (
                      <Button size="sm" variant="outline" onClick={() => reassess(a.subject_id)}
                              disabled={busy === a.subject_id} data-testid={`reassess-${a.subject_id}`}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        {busy === a.subject_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Re-score"}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="screening" className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
          <Panel testId="panel-screen-form" kicker="FR-RISK-002" title="Screen a name">
            <div className="space-y-3 p-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] text-muted-foreground">Legal or individual name</Label>
                <Input value={screen.name} data-testid="screen-name-input"
                       onChange={(e) => setScreen({ ...screen, name: e.target.value })}
                       onKeyDown={(e) => e.key === "Enter" && runScreen()}
                       placeholder="Helios Petro Holdings" className="bg-muted/40" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-muted-foreground">Country</Label>
                  <Input value={screen.country} data-testid="screen-country-input"
                         onChange={(e) => setScreen({ ...screen, country: e.target.value.toUpperCase().slice(0, 2) })}
                         className="bg-muted/40 font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-muted-foreground">Entity</Label>
                  <Select value={screen.entity_type} onValueChange={(v) => setScreen({ ...screen, entity_type: v })}>
                    <SelectTrigger data-testid="screen-entity-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="organisation">Organisation</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={runScreen} disabled={busy === "screen" || !screen.name.trim()}
                      data-testid="run-screen-button"
                      className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "screen" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Search className="mr-2 h-4 w-4" />} Screen against all lists
              </Button>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Fuzzy matched on name and alias with token overlap and containment, against OFAC SDN,
                UN Consolidated, EU Consolidated, UK OFSI, World Bank debarment, a PEP register and
                adverse-media entries held on the platform.
              </p>
            </div>
          </Panel>

          <Panel testId="panel-screen-result" kicker="Result"
                 title={result ? `${result.query} · ${result.outcome.toUpperCase()}` : "No screening run yet"}>
            {!result && (
              <div className="p-6 text-xs text-muted-foreground">
                Enter a name to screen it. Confirmed sanctions matches block onboarding and giving;
                PEP or adverse hits require review.
              </div>
            )}
            {result && (
              <div className="space-y-3 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip testId="screen-outcome" className={OUTCOME_STYLE[result.outcome]}>{result.outcome}</Chip>
                  <span className="text-[11px] text-muted-foreground">
                    {result.confirmed} confirmed of {result.matches.length} candidates ·
                    {" "}{result.lists_checked.length} lists checked
                  </span>
                </div>
                <Table testId="screen-matches-table"
                       head={<><Th>List</Th><Th>Type</Th><Th>Name</Th><Th>Matched on</Th><Th>Programme</Th><Th>Score</Th></>}>
                  {result.matches.length === 0 && <Empty colSpan={6} label="No candidate matches" />}
                  {result.matches.map((m, i) => (
                    <tr key={refOf(i)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(i, refOf(i)))} data-testid={`screen-match-${i}`}>
                      <Td className="text-[11px]">{m.list}</Td>
                      <Td><Chip className={m.type === "sanction" ? OUTCOME_STYLE.block : OUTCOME_STYLE.review}>{m.type}</Chip></Td>
                      <Td className="font-medium text-foreground">{m.name}</Td>
                      <Td className="text-[11px] text-muted-foreground">{m.matched_on}</Td>
                      <Td className="font-mono text-[10.5px]">{m.programme}</Td>
                      <Td><ScoreBar score={m.score} band={m.score >= 85 ? "critical" : m.score >= 62 ? "high" : "medium"} /></Td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="cases" className="mt-4">
          <Panel testId="panel-edd-cases" kicker="FR-RISK-003" title="Enhanced due diligence">
            <Table testId="edd-cases-table"
                   head={<><Th>Case</Th><Th>Subject</Th><Th>Trigger</Th><Th>Level</Th><Th>Status</Th>
                     <Th>Due</Th><Th>Notes</Th><Th /></>}>
              {data.cases.length === 0 && <Empty colSpan={8} label="No due-diligence cases open" />}
              {data.cases.map((k) => (
                <tr key={refOf(k)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(k, refOf(k)))} data-testid={`edd-row-${k.case_ref}`}>
                  <Td className="font-mono text-[11px]">{k.case_ref}</Td>
                  <Td className="font-medium text-foreground">{k.subject_name}</Td>
                  <Td className="text-[11px] text-muted-foreground">{k.trigger}</Td>
                  <Td><Chip className={k.level === "enhanced" ? BAND_STYLE.high : BAND_STYLE.medium}>{k.level}</Chip></Td>
                  <Td><Chip className={k.status === "closed" ? BAND_STYLE.low
                    : k.status === "escalated" ? BAND_STYLE.critical : BAND_STYLE.medium}>{k.status}</Chip></Td>
                  <Td className="font-mono text-[11px]">{k.due_on}</Td>
                  <Td className="font-mono text-[11px]">{(k.notes || []).length}</Td>
                  <Td>
                    <Button size="sm" variant="outline" onClick={() => setActive(k)}
                            data-testid={`open-edd-${k.case_ref}`}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">Open file</Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="lists" className="mt-4">
          <Panel testId="panel-watchlist" kicker="Reference data"
                 title={`${watchlist?.total || 0} watchlist entries held on the platform`}>
            <Table testId="watchlist-table"
                   head={<><Th>List</Th><Th>Type</Th><Th>Name</Th><Th>Aliases</Th><Th>Country</Th><Th>Programme / role</Th></>}>
              {(watchlist?.entries || []).map((w) => (
                <tr key={refOf(w)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(w, refOf(w)))} data-testid={`watchlist-row-${w.id}`}>
                  <Td className="text-[11px]">{w.list}</Td>
                  <Td><Chip className={w.type === "sanction" ? OUTCOME_STYLE.block : OUTCOME_STYLE.review}>{w.type}</Chip></Td>
                  <Td className="font-medium text-foreground">{w.name}</Td>
                  <Td className="text-[11px] text-muted-foreground">{(w.aliases || []).join(", ")}</Td>
                  <Td className="font-mono text-[11px]">{w.country}</Td>
                  <Td className="text-[11px] text-muted-foreground">{w.programme}</Td>
                </tr>
              ))}
            </Table>
            <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              These names are held on the platform as reference data — no external screening provider is called.
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(active)} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl border-border bg-card" data-testid="edd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="h-4 w-4 text-primary" /> {active?.case_ref} · {active?.subject_name}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[["Band", active.risk_band], ["Score", active.risk_score], ["Level", active.level],
                  ["Due", active.due_on]].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-border bg-muted/30 p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="text-[13px] font-semibold text-foreground">{String(v)}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-border p-3 text-[12px] text-muted-foreground">
                <span className="text-foreground">Trigger:</span> {active.trigger} · opened by {active.opened_by}
              </div>

              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Due-diligence trail
                </div>
                {(active.notes || []).length === 0 && (
                  <div className="text-[12px] text-muted-foreground" data-testid="edd-no-notes">
                    Nothing recorded yet. At least one note is required before the case can be decided.
                  </div>
                )}
                {(active.notes || []).map((n, i) => (
                  <div key={i} className="rounded-xl border border-border bg-muted/20 p-2.5" data-testid={`edd-note-${i}`}>
                    <div className="text-[12px] text-foreground">{n.note}</div>
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                      {n.author} · {dateTime(n.at)}{n.evidence_ref ? ` · ${n.evidence_ref}` : ""}
                    </div>
                  </div>
                ))}
                {active.decision && (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-2.5" data-testid="edd-decision">
                    <div className="text-[12px] font-semibold text-foreground">
                      Decision: {active.decision.outcome}
                    </div>
                    <div className="text-[12px] text-muted-foreground">{active.decision.rationale}</div>
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                      {active.decision.decided_by} · {dateTime(active.decision.decided_at)}
                    </div>
                  </div>
                )}
              </div>

              {active.status !== "closed" && can("risk", "write") && (
                <div className="space-y-2 rounded-xl border border-border p-3">
                  <Label className="text-[11px] text-muted-foreground">Add a due-diligence note</Label>
                  <Textarea value={note.note} rows={2} data-testid="edd-note-input"
                            onChange={(e) => setNote({ ...note, note: e.target.value })}
                            placeholder="Source of wealth verified against audited accounts…"
                            className="bg-muted/40 text-[12px]" />
                  <div className="flex gap-2">
                    <Input value={note.evidence_ref} placeholder="Evidence reference"
                           data-testid="edd-evidence-input"
                           onChange={(e) => setNote({ ...note, evidence_ref: e.target.value })}
                           className="bg-muted/40 font-mono text-[12px]" />
                    <Button onClick={addNote} disabled={busy === "note" || note.note.trim().length < 5}
                            data-testid="edd-add-note-button"
                            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      {busy === "note" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Record
                    </Button>
                  </div>
                </div>
              )}

              {active.status !== "closed" && can("risk", "approve") && (
                <div className="space-y-2 rounded-xl border border-border p-3">
                  <Label className="text-[11px] text-muted-foreground">Decide the case</Label>
                  {active.opened_by === me?.email && me?.role !== "platform_admin" && (
                    <div className="rounded-lg bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300"
                         data-testid="edd-four-eyes-warning">
                      You opened this case, so you may only escalate it — someone else must accept or reject.
                    </div>
                  )}
                  <Select value={decision.outcome} onValueChange={(v) => setDecision({ ...decision, outcome: v })}>
                    <SelectTrigger data-testid="edd-outcome-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accept">Accept the relationship</SelectItem>
                      <SelectItem value="reject">Reject and block the donor</SelectItem>
                      <SelectItem value="escalate">Escalate to the board</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea value={decision.rationale} rows={2} data-testid="edd-rationale-input"
                            onChange={(e) => setDecision({ ...decision, rationale: e.target.value })}
                            placeholder="Rationale recorded on the audit trail…" className="bg-muted/40 text-[12px]" />
                  <Button onClick={decide} disabled={busy === "decide" || decision.rationale.trim().length < 5}
                          data-testid="edd-decide-button"
                          className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    {busy === "decide" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record decision
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {detail.sheet}
    </div>
  );
}
